import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaLaptopCode, FaDatabase, FaCloud, FaServer, FaCogs, FaShieldAlt,
    FaChartLine, FaCheckCircle, FaChevronDown, FaChevronUp,
    FaCode, FaIndustry, FaBuilding, FaHeartbeat, FaGraduationCap,
    FaJava, FaDocker, FaAws, FaLightbulb, FaPencilRuler, FaRocket, FaBug, FaHeadset, FaChevronRight
} from 'react-icons/fa';
import {
    SiPython, SiDotnet, SiPostgresql, SiMongodb, SiKubernetes, SiNodedotjs
} from 'react-icons/si';
import heroImage from '../../assets/download.png';

const SoftwareDevelopmentPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaLaptopCode, title: 'Custom Software Development', desc: 'Tailor-made software solutions designed to address your specific business challenges and goals.', image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600' },
        { icon: FaCloud, title: 'SaaS Development', desc: 'Scalable and secure Software-as-a-Service platforms built for global accessibility and growth.', image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600' },
        { icon: FaServer, title: 'Enterprise ERP Solutions', desc: 'Integrated management of core business processes, often in real-time and mediated by software.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600' },
        { icon: FaDatabase, title: 'Database & API Integration', desc: 'Robust data management and seamless third-party API integrations for unified workflows.', image: 'https://images.unsplash.com/photo-1544383215-e65d34622709?w=600' },
        { icon: FaShieldAlt, title: 'Legacy Modernization', desc: 'Upgrading outdated systems to modern technologies to improve performance and security.', image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600' },
        { icon: FaCogs, title: 'Automation & AI Tools', desc: 'Smart automation scripts and AI-driven tools to reduce manual effort and errors.', image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600' }
    ];

    const technologies = [
        { name: 'Python', icon: SiPython, desc: 'Powerful backend logic & AI' },
        { name: 'Java', icon: FaJava, desc: 'Enterprise-grade stability' },
        { name: 'C# / .NET', icon: SiDotnet, desc: 'Microsoft ecosystem integration' },
        { name: 'Node.js', icon: SiNodedotjs, desc: 'Scalable API services' },
        { name: 'PostgreSQL', icon: SiPostgresql, desc: 'Reliable relational database' },
        { name: 'MongoDB', icon: SiMongodb, desc: 'Flexible NoSQL storage' },
        { name: 'Docker', icon: FaDocker, desc: 'Containerized deployment' },
        { name: 'AWS', icon: FaAws, desc: 'Cloud infrastructure' }
    ];

    const process = [
        { step: '01', title: 'Requirement Analysis', desc: 'In-depth analysis of business needs, user personas, and technical feasibility.' },
        { step: '02', title: 'System Architecture', desc: 'Designing robust, scalable, and secure software architecture blueprints.' },
        { step: '03', title: 'Agile Development', desc: 'Iterative coding process with regular sprints and stakeholder feedback.' },
        { step: '04', title: 'Quality Assurance', desc: 'Comprehensive testing including unit, integration, and user acceptance testing.' },
        { step: '05', title: 'Deployment & CI/CD', desc: 'Automated deployment pipelines ensuring smooth production rollouts.' },
        { step: '06', title: 'Maintenance & Support', desc: '24/7 monitoring, bug fixes, and feature enhancements post-launch.' }
    ];

    const flowSteps = [
        { title: 'Plan', icon: FaLightbulb, color: 'from-yellow-400 to-orange-500' },
        { title: 'Design', icon: FaPencilRuler, color: 'from-pink-500 to-rose-500' },
        { title: 'Develop', icon: FaCode, color: 'from-blue-500 to-cyan-500' },
        { title: 'Test', icon: FaBug, color: 'from-purple-500 to-indigo-500' },
        { title: 'Deploy', icon: FaRocket, color: 'from-green-400 to-emerald-600' },
        { title: 'Scale', icon: FaChartLine, color: 'from-orange-400 to-red-500' }
    ];

    const faqs = [
        { q: 'What is Custom Software Development?', a: 'It involves designing, developing, deploying, and maintaining software tailored to a specific set of users, functions, or organizations, unlike off-the-shelf software.' },
        { q: 'How do you ensure data security?', a: 'We implement industry-standard security protocols including OAuth, SSL/TLS, data encryption, and regular security audits.' },
        { q: 'Can you update my existing legacy software?', a: 'Yes, we specialize in legacy modernization, refactoring old codebases to modern languages and cloud infrastructures.' },
        { q: 'What is the typical timeline for a project?', a: 'Timelines vary by complexity. A simple tool might take 4 weeks, while a full ERP system could take 6+ months.' },
        { q: 'Do I own the source code?', a: 'Yes, typically upon full payment, the intellectual property rights and source code are transferred to you.' }
    ];

    const whyChooseUs = [
        { title: 'Domain Expertise', desc: 'Years of experience delivering complex software across various industries.' },
        { title: 'Agile Methodology', desc: 'Flexible development process that adapts to changing business requirements.' },
        { title: 'Scalable Solutions', desc: 'Software built to grow with your business, handling increased loads effortlessly.' },
        { title: 'Security First', desc: 'Security baked into the development lifecycle, not just an afterthought.' },
        { title: 'Transparent Communication', desc: 'Regular updates and demos to ensure total alignment with your vision.' },
        { title: 'Post-Delivery Support', desc: 'Dedicated support teams to ensure maximum uptime and performance.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-bottom"
                    style={{ backgroundImage: `url(${heroImage})` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-cyan-900/80 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-blue-300 mb-3">What We Do &gt; Engineering</p>
                        <h1 className="text-4xl md:text-5xl font-light mb-4 leading-tight">Software Development</h1>
                        <p className="text-base font-light text-gray-200 leading-relaxed mb-6">
                            Building intelligent, scalable, and secure software solutions for modern enterprises
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="#inquiry" className="px-5 py-2 bg-white text-blue-600 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                                Get Free Consultation
                            </a>
                            <a href="#services" className="px-5 py-2 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-blue-600 transition-all text-sm whitespace-nowrap">
                                Explore Solutions
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Development Lifecycle (App Page Style Grid) */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Software Development Lifecycle</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            A systematic approach to engineering robust software systems tailored to your needs.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 mt-16">
                        {process.slice(0, 3).map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="text-center"
                            >
                                <div className="text-6xl font-bold text-blue-200 mb-4">{item.step}</div>
                                <h3 className="text-xl font-medium text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-600">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 mt-8">
                        {process.slice(3).map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="text-center"
                            >
                                <div className="text-6xl font-bold text-blue-200 mb-4">{item.step}</div>
                                <h3 className="text-xl font-medium text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-600">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Technology Stack</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Leveraging modern frameworks and tools to build future-proof software.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {technologies.map((tech, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="p-6 bg-slate-50 rounded-sm border border-slate-200 text-center hover:shadow-lg transition-all group"
                            >
                                <tech.icon className="text-5xl mx-auto mb-4 text-blue-600 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">{tech.name}</h3>
                                <p className="text-sm text-slate-600">{tech.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (HCL Style Hover Cards) */}
            <section id="services" className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Software Solutions</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            End-to-end software development services for varied industry needs.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-64 overflow-hidden rounded-sm border border-slate-200 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}>
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80"></div>
                                </div>
                                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white z-10">
                                    <div>
                                        <service.icon className="text-4xl mb-4" />
                                        <h3 className="text-xl font-medium mb-2">{service.title}</h3>
                                    </div>
                                    <motion.div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-600 to-cyan-600 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                                        <p className="text-white text-sm leading-relaxed">{service.desc}</p>
                                        <button className="mt-4 text-white font-medium hover:underline">Learn More →</button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-20 bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light mb-6">Why Partner With Us?</h2>
                        <p className="text-lg text-blue-100 max-w-3xl mx-auto">
                            We deliver technical excellence with direct business value.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {whyChooseUs.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20 hover:bg-white/20 transition-all"
                            >
                                <FaCheckCircle className="text-3xl text-green-300 mb-4" />
                                <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                                <p className="text-blue-100 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Horizontal Colorful with BIG VISIBLE ARROWS) */}
            <section className="py-24 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Development Flow</h2>
                        <p className="text-slate-600">A seamless journey from concept to deployment.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-1 bg-slate-200 md:rounded-full -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-0 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">

                                    {/* ARROW: Only show BETWEEN steps (not after the last one) */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="hidden md:flex absolute top-[30px] -right-[50%] w-full justify-center items-center z-10 pointer-events-none">
                                            <motion.div
                                                animate={{ x: [-3, 3, -3] }}
                                                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                                            >
                                                <FaChevronRight className="text-2xl text-slate-400 font-bold" />
                                            </motion.div>
                                        </div>
                                    )}

                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5, y: 20 }}
                                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-3xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300 border-4 border-white ring-4 ring-slate-50`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Number Badge */}
                                    <div className="absolute top-0 right-1/2 translate-x-10 -translate-y-0 w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-md z-30">
                                        {idx + 1}
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 group-hover:text-blue-600 transition-colors mt-2">{step.title}</h3>

                                    {/* Mobile Vertical Line */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute bottom-[-40px] left-1/2 w-0.5 h-[40px] bg-slate-300"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-12">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Frequently Asked Questions</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="border border-slate-200 rounded-sm overflow-hidden"
                                    >
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center bg-slate-50 hover:bg-slate-100 transition-colors"
                                        >
                                            <span className="font-medium text-slate-900">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-blue-600" /> : <FaChevronDown className="text-slate-400" />}
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 py-4 bg-white">
                                                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-8 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Get a Free Quote</h2>
                            <p className="text-slate-600 mb-6">Fill out the form below to kickstart your software project.</p>

                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                                    <input type="text" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600" placeholder="John Doe" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                                        <input type="email" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600" placeholder="john@example.com" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                                        <input type="tel" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600" placeholder="+91 98765 43210" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Project Type</label>
                                    <select className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                                        <option>Custom Software</option>
                                        <option>SaaS Platform</option>
                                        <option>Enterprise ERP</option>
                                        <option>Legacy Modernization</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                                    <textarea rows="4" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-600" placeholder="Tell us about your requirements..."></textarea>
                                </div>
                                <button type="submit" className="w-full bg-blue-600 text-white font-medium py-3 rounded-sm hover:bg-blue-700 transition-colors shadow-lg">
                                    Send Inquiry
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-700 text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl md:text-5xl font-light mb-6">Ready to Scale Your Business?</h2>
                    <p className="text-xl font-light mb-8 text-blue-100">
                        Let's engineer the future of your company together.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="#inquiry" className="inline-block px-6 py-2.5 bg-white text-blue-600 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                            Contact Us Today
                        </a>
                        <a href="tel:+919876543210" className="inline-block px-6 py-2.5 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-blue-600 transition-all text-sm whitespace-nowrap">
                            Call Us Now
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SoftwareDevelopmentPage;
